import {
  APIReferenceAttributes,
  APITagAttributes,
  ArdoqBlob,
  ArdoqId,
  ComponentBatchDeleteResponse,
  SubtreeInfo,
  Named,
  Persisted,
  Versioned,
  APIDiffContext,
  APIResetRequest,
  MergeTarget,
  UnpersistedEntity,
  UpdateScenarioPayload,
  AddToScenarioResponse,
  MergeComponentApiResponse,
  MergeReferenceApiResponse,
  APIComponentAttributes,
  AllSupportedAPIAttributes,
  getEntityType,
  APIEntityType,
  APIScenarioAttributes,
  APIScenarioScopeData,
  Described,
  CustomFields,
  CreateScenarioAttributes,
  CreateScenarioResponse,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  toRequestOptions,
  Method,
  fetchJson,
  headers,
  fetchVoid,
  fetchVoidAndRespondWithId,
  fetchEntities,
  renameEntity,
  deleteEntity,
} from './api';

const deleteComponents = (
  scenarioId: ArdoqId,
  componentIds: ArdoqId[]
): ApiResponse<ComponentBatchDeleteResponse> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/component`,
    toRequestOptions(Method.DELETE, { componentIds })
  );
  return fetchJson<ComponentBatchDeleteResponse>(request);
};

const deleteExternalDocument = (
  scenarioId: ArdoqId,
  externalDocumentId: ArdoqId
): ApiResponse<void> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/external-document/${externalDocumentId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoid(request);
};

const removeWorkspace = async ({
  scenarioId,
  workspaceId,
}: {
  scenarioId: ArdoqId;
  workspaceId: ArdoqId;
}): ApiResponse<ArdoqId> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/remove-workspace-from-scenario`,
    toRequestOptions(Method.PUT, { workspaceId })
  );
  return fetchVoidAndRespondWithId(request, scenarioId);
};

const subtreeInfo = async (
  scenarioId: ArdoqId,
  componentIds: ArdoqId[]
): ApiResponse<SubtreeInfo> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/component/subtree-info`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson<SubtreeInfo>(request);
};

const updateBranchOff = async (scenarioId: ArdoqId): ApiResponse<void> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/update-branch-off`,
    toRequestOptions(Method.PUT, null)
  );
  return fetchVoid(request);
};

const isInSync = async (scenarioId: ArdoqId): ApiResponse<boolean> => {
  const request = new Request(`/api/scenario/${scenarioId}/insync`, {
    headers,
  });
  return fetchJson<boolean>(request);
};

const canAddToScenario = (
  scenarioId: ArdoqId,
  componentIds: ArdoqId[]
): ApiResponse<boolean> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/can-add`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson<boolean>(request);
};

const addToScenario = (
  payload: UpdateScenarioPayload
): ApiResponse<AddToScenarioResponse> => {
  const request = new Request(
    `/api/scenario/${payload.scenarioId}/add-to-scenario`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson<AddToScenarioResponse>(request);
};

const removeFromScenario = (
  payload: UpdateScenarioPayload
): ApiResponse<AddToScenarioResponse> => {
  const request = new Request(
    `/api/scenario/${payload.scenarioId}/remove-from-scenario`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson<AddToScenarioResponse>(request);
};

const create = (
  scenarioAttributes: CreateScenarioAttributes
): ApiResponse<CreateScenarioResponse> => {
  const request = new Request(
    '/api/scenario/create',
    toRequestOptions(Method.POST, scenarioAttributes)
  );
  return fetchJson<CreateScenarioResponse>(request);
};

type APICopyResponse = Persisted &
  Versioned &
  ArdoqBlob &
  Named & { scopeId: ArdoqId; branchId: ArdoqId };

const copy = (
  scenarioId: ArdoqId,
  name: string
): ApiResponse<APICopyResponse> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/copy`,
    toRequestOptions(Method.POST, { name })
  );
  return fetchJson<APICopyResponse>(request);
};

type ScenarioReferencesResponse = {
  references: APIReferenceAttributes[];
  referencesToScope: APIReferenceAttributes[];
};

const getScenarioReferences = (
  componentIds: ArdoqId[],
  scopeId?: ArdoqId,
  branchId?: ArdoqId
): ApiResponse<ScenarioReferencesResponse> => {
  const urlParams = new URLSearchParams();
  if (scopeId && branchId) {
    urlParams.set('scopeId', scopeId);
    urlParams.set('branchId', branchId);
  }
  const request = new Request(
    `/api/scenario/references?${urlParams.toString()}`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson<ScenarioReferencesResponse>(request);
};

type NumberOfRelationsOutsideScope = {
  numberOfDescendantsOutsideScope: number;
  numberOfReferencesOutsideScope: number;
};

const getNumberOfRelationsOutsideScope = (
  scenarioId: ArdoqId,
  componentIds: ArdoqId[]
): ApiResponse<NumberOfRelationsOutsideScope> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/number-of-relations-outside-scope`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson<NumberOfRelationsOutsideScope>(request);
};

const load = (
  scenarioId: ArdoqId
): ApiResponse<APIScenarioScopeData & { inSync: boolean }> => {
  const request = new Request(`/api/scenario/${scenarioId}/aggregated/branch`, {
    headers,
  });
  return fetchJson<APIScenarioScopeData & { inSync: boolean }>(request);
};

const diffContext = (scenarioId: ArdoqId): ApiResponse<APIDiffContext> => {
  const request = new Request(`/api/scenario/${scenarioId}/diffcontext`, {
    headers,
  });
  return fetchJson<APIDiffContext>(request);
};

const reset = (
  scenarioId: ArdoqId,
  body: APIResetRequest,
  mergeTarget: MergeTarget
): ApiResponse<void> => {
  const url = new URL(
    `/api/scenario/${scenarioId}/reset`,
    window.location.origin
  );
  url.searchParams.set('mergeTarget', mergeTarget);

  const request = new Request(url, toRequestOptions(Method.POST, body));
  return fetchVoid(request);
};

const mergeEnitites = ({
  mergedEntity,
  entityIdsToDelete,
  scenarioId,
}: {
  mergedEntity: AllSupportedAPIAttributes;
  entityIdsToDelete: ArdoqId[];
  scenarioId: ArdoqId;
}) => {
  if (getEntityType(mergedEntity) === APIEntityType.COMPONENT) {
    return mergeComponents({
      mergedEntity: mergedEntity as APIComponentAttributes,
      entityIdsToDelete,
      scenarioId,
    });
  }
  return mergeReferences({
    mergedEntity: mergedEntity as APIReferenceAttributes,
    entityIdsToDelete,
    scenarioId,
  });
};

const mergeComponents = ({
  mergedEntity,
  entityIdsToDelete,
  scenarioId,
}: {
  mergedEntity: APIComponentAttributes;
  entityIdsToDelete: ArdoqId[];
  scenarioId: ArdoqId;
}): ApiResponse<MergeComponentApiResponse> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/component/${mergedEntity._id}/merge`,
    toRequestOptions(Method.PUT, {
      'subsumed-component-ids': entityIdsToDelete,
      'merged-component-data': mergedEntity,
    })
  );
  return fetchJson<MergeComponentApiResponse>(request);
};

const updateComponent = ({
  component,
  scenarioId,
}: {
  component: APIComponentAttributes;
  scenarioId: ArdoqId;
}): ApiResponse<APIComponentAttributes> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/component/${component._id}`,
    toRequestOptions(Method.PUT, component)
  );
  return fetchJson<APIComponentAttributes>(request);
};

const mergeReferences = ({
  mergedEntity,
  entityIdsToDelete,
  scenarioId,
}: {
  mergedEntity: APIReferenceAttributes;
  entityIdsToDelete: ArdoqId[];
  scenarioId: ArdoqId;
}): ApiResponse<MergeReferenceApiResponse> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/reference/${mergedEntity._id}/merge`,
    toRequestOptions(Method.PUT, {
      'subsumed-reference-ids': entityIdsToDelete,
      'merged-reference-data': mergedEntity,
    })
  );
  return fetchJson<MergeReferenceApiResponse>(request);
};

export type ComponentCreate = Named &
  Described &
  CustomFields & {
    _order?: number;
    color?: string;
    icon?: string;
    image?: string;
    lock?: string | null;
    model: ArdoqId;
    parent: ArdoqId | null;
    rootWorkspace: ArdoqId;
    shape?: string;
    typeId: ArdoqId;
  };

const createComponent = ({
  component,
  scenarioId,
}: {
  component: ComponentCreate;
  scenarioId: ArdoqId;
}): ApiResponse<APIComponentAttributes> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/component`,
    toRequestOptions(Method.POST, component)
  );
  return fetchJson<APIComponentAttributes>(request);
};

const createReference = (
  reference: UnpersistedEntity<APIReferenceAttributes>,
  scenarioId: ArdoqId
): ApiResponse<APIReferenceAttributes> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/reference`,
    toRequestOptions(Method.POST, reference)
  );
  return fetchJson<APIReferenceAttributes>(request);
};

const deleteReference = (
  referenceId: ArdoqId,
  scenarioId: ArdoqId
): ApiResponse<ArdoqId> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/reference/${referenceId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoidAndRespondWithId(request, referenceId);
};

const updateReference = (
  reference: APIReferenceAttributes,
  scenarioId: ArdoqId
): ApiResponse<APIReferenceAttributes> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/reference/${reference._id}`,
    toRequestOptions(Method.PUT, reference)
  );
  return fetchJson<APIReferenceAttributes>(request);
};

const createTag = (
  tag: UnpersistedEntity<APITagAttributes>,
  scenarioId: ArdoqId
): ApiResponse<APITagAttributes> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/tag`,
    toRequestOptions(Method.POST, tag)
  );
  return fetchJson<APITagAttributes>(request);
};

const deleteTag = (
  tagId: ArdoqId,
  scenarioId: ArdoqId
): ApiResponse<ArdoqId> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/tag/${tagId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoidAndRespondWithId(request, tagId);
};

const updateTag = (
  tag: APITagAttributes,
  scenarioId: ArdoqId
): ApiResponse<APITagAttributes> => {
  const request = new Request(
    `/api/scenario/${scenarioId}/tag/${tag._id}`,
    toRequestOptions(Method.PUT, tag)
  );
  return fetchJson<APITagAttributes>(request);
};

export const scenarioApi = {
  deleteComponents,
  deleteExternalDocument,
  removeWorkspace,
  updateBranchOff,
  isInSync,
  canAddToScenario,
  getScenarioReferences,
  getNumberOfRelationsOutsideScope,
  subtreeInfo,
  create,
  copy,
  load,
  diffContext,
  reset,
  addToScenario,
  removeFromScenario,
  mergeEnitites,
  fetchAll: fetchEntities<APIScenarioAttributes>('scenario'),
  rename: renameEntity<APIScenarioAttributes>('scenario'),
  delete: deleteEntity('scenario'),
  createComponent,
  createReference,
  createTag,
  deleteReference,
  deleteTag,
  updateComponent,
  updateReference,
  updateTag,
};
